import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { formatDate, fullMonthAndYear, shortMonthAndShortYear } from "@similarweb/pro-logic-components";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";

import { ConversionRateOverviewPopCards } from "./ConversionRateOverviewPopCards";

interface IConversionRateOverviewPopProps {
    primaryCategory: ICategory | ICustomCategory;
    availabilityState: "available" | "locked";
}

export const ConversionRateOverviewPop = ({ primaryCategory, availabilityState }: IConversionRateOverviewPopProps) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();

    const country = params.country as number;
    const includeSubdomains = (params.includeSubdomains as boolean | undefined) ?? true;
    const durationParam = (params.duration as string) ?? BasicDurations.LAST_MONTH;
    const comparedDurationParam = params.comparedDuration as string | undefined;
    const {
        iso: { from, to, compareFrom, compareTo },
        isWindow,
    } = DurationService.getDurationData(durationParam, comparedDurationParam).forAPI;

    if (!compareFrom || !compareTo) {
        return null;
    }

    const countryText = CountryService.getCountryById(country)?.text ?? "";
    const dateRangeText = formatDate(from, to, fullMonthAndYear);
    const currentRowLabel = formatDate(from, to, shortMonthAndShortYear);
    const comparedRowLabel = formatDate(compareFrom, compareTo, shortMonthAndShortYear);

    const isYearOverYear = comparedDurationParam === BasicDurations.LAST_TWELVE_MONTHS;
    const changeSuffix = translate(
        isYearOverYear
            ? CONVERSION_RATE_TRANSLATION_KEYS.popChangeSuffixYoY
            : CONVERSION_RATE_TRANSLATION_KEYS.popChangeSuffixPoP,
    );

    return (
        <ConversionRateOverviewPopCards
            primaryCategory={primaryCategory}
            availabilityState={availabilityState}
            country={country}
            countryText={countryText}
            includeSubdomains={includeSubdomains}
            from={from}
            to={to}
            compareFrom={compareFrom}
            compareTo={compareTo}
            isWindow={isWindow}
            dateRangeText={dateRangeText}
            currentRowLabel={currentRowLabel}
            comparedRowLabel={comparedRowLabel}
            changeSuffix={changeSuffix}
        />
    );
};
